import { GenericTable } from "@/components/GenericTable/GenericTable";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useConditionByOrganization } from "@/services/fhir/condition/hooks/useConditionByOrganization";
import {
  Box,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Skeleton,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  Tab,
  Tabs,
  Tooltip,
  IconButton,
  useTheme,
  Divider,
} from "@mui/material";
import { NoData } from "@/components/common/NoData";
import { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import SearchIcon from "@mui/icons-material/Search";

// Função para calcular a largura ideal do eixo Y baseada no tamanho dos labels
const calculateYAxisWidth = (data: { label: string; value: number }[]) => {
  if (!data || data.length === 0) return 150;
  const maxLabelLength = Math.max(...data.map((item) => item.label.length));
  const calculatedWidth = Math.max(150, maxLabelLength * 7 + 20);
  return Math.min(calculatedWidth, 400);
};
import { useDict } from "@/utils/useDict";
import { useAdministrativeFilters } from "@/pages/adminInsights/shared/useAdministrativeFilters";
import { useDateRangeFilter } from "@/components/common/DateRangeFilter/useDateRangeFilter";
import { ViewHeader } from "@/components/schedule/components/ViewHeader";
import { DateFilterType } from "@/utils/interfaces";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`diagnostic-tabpanel-${index}`}
      aria-labelledby={`diagnostic-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface VerificationStatusStats {
  status: string;
  count: number;
  percentage: number;
}

interface DiagnosticDetail {
  code: string;
  label: string;
  count: number;
  percentage: number;
}

interface PatientDiagnosticData {
  diagnostic: string;
  code: string;
  verificationStatus: string;
  date: string;
}

interface PatientDiagnosticGroup {
  patientId: string;
  patientName: string;
  totalDiagnostics: number;
  diagnostics: PatientDiagnosticData[];
  statusCounts: {
    confirmed: number;
    unconfirmed: number;
    provisional: number;
    refuted: number;
    "entered-in-error": number;
    unknown: number;
  };
}

export function DiagnosticAnalysis({
  count,
  data,
}: {
  count?: number;
  data?: any;
}) {
  const theme = useTheme();
  const { organizationId } = useOrganizationContext();
  const [currentTab, setCurrentTab] = useState(0);
  const [search, setSearch] = useState<string>("");
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [patientDialogOpen, setPatientDialogOpen] = useState<boolean>(false);
  const [patientDiagnostics, setPatientDiagnostics] = useState<
    PatientDiagnosticData[]
  >([]);
  const [dialogSearch, setDialogSearch] = useState<string>("");
  const [diagnosticSearch, setDiagnosticSearch] = useState<string>("");

  // Filtros de data
  const {
    selectedDate,
    dateFilterType,
    dateRange,
    handleDateChange,
    handleFilterTypeChange,
  } = useDateRangeFilter({ defaultFilterType: "month" });

  // Converte dateFilterType para DateFilterType (removendo "year" se necessário)
  const filterType: DateFilterType =
    dateFilterType === "year" ? "month" : (dateFilterType as DateFilterType);

  // Wrapper para handleFilterTypeChange que converte "year" para "month"
  const handleFilterTypeChangeWrapper = (type: DateFilterType) => {
    handleFilterTypeChange(type as any);
  };

  // Filtros administrativos
  const {
    selectedLocation,
    selectedPractitioner,
    handleLocationChange,
    handlePractitionerChange,
    location,
    locationLoading,
    practitionerRole,
    practitioner,
    practitionerRoleLoading,
  } = useAdministrativeFilters({});

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Função para resetar todos os filtros
  const handleReset = () => {
    handleLocationChange("");
    handlePractitionerChange("");
  };

  const {
    data: conditionData,
    loading: conditionLoading,
    fetchConditions,
  } = useConditionByOrganization({
    organizationId,
    startDate: dateRange?.startDate,
    endDate: dateRange?.endDate,
    count: count || 250,
    includeEncounter: false,
    includePatient: true,
  });

  // Processar os dados por verificationStatus
  const { verificationStatusStats, totalRecords } = useMemo(() => {
    const statusMap = new Map<string, number>();
    const conditions = conditionData?.conditions || [];

    for (const cond of conditions) {
      // Obter verificationStatus
      const verificationStatus =
        cond.verificationStatus?.coding?.[0]?.code || "unknown";
      statusMap.set(
        verificationStatus,
        (statusMap.get(verificationStatus) || 0) + 1
      );
    }

    const total = conditions.length;
    const stats: VerificationStatusStats[] = Array.from(statusMap.entries())
      .map(([status, count]) => ({
        status,
        count,
        percentage: total > 0 ? (count / total) * 100 : 0,
      }))
      .sort((a, b) => b.count - a.count);

    return {
      verificationStatusStats: stats,
      totalRecords: total,
    };
  }, [conditionData?.conditions]);

  // Processar diagnósticos detalhados para a tabela
  const { diagnosticDetails } = useMemo(() => {
    const counts = new Map<string, number>();
    const labels = new Map<string, string>();
    const conditions = conditionData?.conditions || [];

    for (const cond of conditions) {
      const codingWithCode = cond.code?.coding?.find((c) => !!c.code);
      const codeKey = codingWithCode?.code || "Não informado";
      const displayLabel =
        codingWithCode?.display || cond.code?.text || codeKey;

      counts.set(codeKey, (counts.get(codeKey) || 0) + 1);
      if (!labels.has(codeKey)) labels.set(codeKey, displayLabel);
    }

    const total = conditions.length;
    const details: DiagnosticDetail[] = Array.from(counts.entries())
      .map(([code, count]) => ({
        code,
        label: labels.get(code) || code,
        count,
        percentage: total > 0 ? (count / total) * 100 : 0,
      }))
      .sort((a, b) => b.count - a.count);

    return {
      diagnosticDetails: details,
    };
  }, [conditionData?.conditions]);

  // Dados para o gráfico de verificação
  const verificationStatusChartData = useMemo(() => {
    return verificationStatusStats.map((stat) => ({
      label:
        useDict.verificationStatus[stat.status] ||
        stat.status ||
        "Não informado",
      value: stat.count,
    }));
  }, [verificationStatusStats]);

  // Processar todos os diagnósticos agrupados
  const allDiagnosticsData = useMemo(() => {
    return diagnosticDetails
      .map((d) => ({
        label: d.label,
        value: d.count,
        code: d.code,
      }))
      .sort((a, b) => b.value - a.value);
  }, [diagnosticDetails]);

  // Processar top 5 diagnósticos para o gráfico
  const topDiagnosticsData = useMemo(() => {
    return allDiagnosticsData.slice(0, 5);
  }, [allDiagnosticsData]);

  // Filtrar diagnósticos para a tabela de pesquisa
  const filteredDiagnosticsData = useMemo(() => {
    if (!diagnosticSearch.trim()) return allDiagnosticsData;
    const q = diagnosticSearch.toLowerCase();
    return allDiagnosticsData.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.code?.toLowerCase().includes(q)
    );
  }, [allDiagnosticsData, diagnosticSearch]);

  // Filtrar diagnósticos detalhados baseado na busca
  const filteredDiagnostics = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return diagnosticDetails;
    return diagnosticDetails.filter(
      (d) =>
        d.label?.toLowerCase().includes(q) || d.code?.toLowerCase().includes(q)
    );
  }, [diagnosticDetails, search]);

  // Agrupar diagnósticos por paciente
  const patientGroups = useMemo(() => {
    const groups = new Map<string, PatientDiagnosticGroup>();
    const conditions = conditionData?.conditions || [];
    const patients = conditionData?.patients || [];
    const patientMap = new Map<string, fhir5.Patient>();

    // Criar mapa de pacientes para acesso rápido
    patients.forEach((patient) => {
      if (patient.id) {
        patientMap.set(patient.id, patient);
      }
    });

    for (const cond of conditions) {
      const patientRef = cond.subject?.reference;
      if (!patientRef) continue;

      const patientId = patientRef.split("/").pop() || "";
      if (!patientId) continue;

      const patient = patientMap.get(patientId);
      const patientName =
        patient?.name?.[0]?.text ||
        `${patient?.name?.[0]?.given?.join(" ") || ""} ${
          patient?.name?.[0]?.family || ""
        }`.trim() ||
        "Não informado";

      if (!groups.has(patientId)) {
        groups.set(patientId, {
          patientId,
          patientName,
          totalDiagnostics: 0,
          diagnostics: [],
          statusCounts: {
            confirmed: 0,
            unconfirmed: 0,
            provisional: 0,
            refuted: 0,
            "entered-in-error": 0,
            unknown: 0,
          },
        });
      }

      const group = groups.get(patientId)!;
      const codingWithCode = cond.code?.coding?.find((c) => !!c.code);
      const diagnosticCode = codingWithCode?.code || "Não informado";
      const diagnosticLabel =
        codingWithCode?.display || cond.code?.text || diagnosticCode;
      const verificationStatus =
        cond.verificationStatus?.coding?.[0]?.code || "unknown";
      const recordedDate = cond.recordedDate
        ? new Date(cond.recordedDate).toLocaleDateString()
        : "Não informado";

      group.totalDiagnostics++;
      group.diagnostics.push({
        diagnostic: diagnosticLabel,
        code: diagnosticCode,
        verificationStatus,
        date: recordedDate,
      });

      // Contar status
      if (verificationStatus in group.statusCounts) {
        group.statusCounts[
          verificationStatus as keyof typeof group.statusCounts
        ]++;
      } else {
        group.statusCounts.unknown++;
      }
    }

    return Array.from(groups.values()).sort(
      (a, b) => b.totalDiagnostics - a.totalDiagnostics
    );
  }, [conditionData?.conditions, conditionData?.patients]);

  // Filtrar grupos de pacientes baseado na busca
  const filteredPatientGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? patientGroups.filter(
          (group) =>
            group.patientName?.toLowerCase().includes(q) ||
            group.diagnostics.some(
              (d) =>
                d.diagnostic?.toLowerCase().includes(q) ||
                d.code?.toLowerCase().includes(q)
            )
        )
      : patientGroups;

    return filtered.map((group) => ({
      patientName: group.patientName,
      totalDiagnostics: group.totalDiagnostics,
      confirmed: group.statusCounts.confirmed,
      unconfirmed: group.statusCounts.unconfirmed,
      provisional: group.statusCounts.provisional,
      refuted: group.statusCounts.refuted,
      enteredInError: group.statusCounts["entered-in-error"],
      unknown: group.statusCounts.unknown,
    }));
  }, [patientGroups, search]);

  // Filtrar diagnósticos do dialog baseado na busca
  const filteredDialogDiagnostics = useMemo(() => {
    const q = dialogSearch.trim().toLowerCase();
    return q
      ? patientDiagnostics.filter(
          (diagnostic) =>
            diagnostic.diagnostic?.toLowerCase().includes(q) ||
            diagnostic.code?.toLowerCase().includes(q) ||
            diagnostic.verificationStatus?.toLowerCase().includes(q)
        )
      : patientDiagnostics;
  }, [patientDiagnostics, dialogSearch]);

  // Função para lidar com o clique na linha da tabela de pacientes
  const handlePatientRowClick = (row: any) => {
    const group = patientGroups.find((g) => g.patientName === row.patientName);
    if (group) {
      setSelectedPatient(group);
      setPatientDiagnostics(group.diagnostics);
      setDialogSearch("");
      setPatientDialogOpen(true);
    }
  };

  useEffect(() => {
    if (organizationId) {
      fetchConditions();
    }
  }, [
    organizationId,
    fetchConditions,
    dateRange,
    selectedLocation,
    selectedPractitioner,
  ]);

  // Renderizar skeleton durante carregamento
  if (conditionLoading) {
    return (
      <Stack spacing={3} width="100%">
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Skeleton variant="text" width={200} height={32} />
              <Skeleton variant="rectangular" height={250} />
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack width="100%" spacing={3}>
      {/* Barra de Filtros */}
      <ViewHeader
        title="Diagnósticos"
        dateFilter={selectedDate}
        filterType={filterType}
        setDateFilter={handleDateChange}
        setFilterType={handleFilterTypeChangeWrapper}
        showAdminFilters={true}
        adminFiltersProps={{
          selectedLocation,
          selectedPractitioner,
          onLocationChange: handleLocationChange,
          onPractitionerChange: handlePractitionerChange,
          location,
          locationLoading,
          practitionerRole,
          practitioner,
          practitionerRoleLoading,
          onResetFilters: () => {
            handleLocationChange("");
            handlePractitionerChange("");
          },
          setIsSearchActive: () => {},
        }}
        onReset={handleReset}
        resetButtonProps={{
          tooltip: "Limpar todos os filtros e voltar ao estado inicial",
        }}
      />

      {totalRecords > 0 ? (
        <>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={currentTab} onChange={handleTabChange}>
              <Tab label="Status de Verificação" />
              <Tab label="Detalhamento" />
            </Tabs>
          </Box>

          {/* Aba de Status de Verificação */}
          <TabPanel value={currentTab} index={0}>
            <Card>
              <CardContent>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  {verificationStatusChartData.length > 0
                    ? (() => {
                        const sorted = [...verificationStatusChartData].sort(
                          (a, b) => b.value - a.value
                        );
                        const total = verificationStatusChartData.reduce(
                          (acc, item) => acc + item.value,
                          0
                        );
                        const percentage =
                          total > 0
                            ? Math.round((sorted[0].value / total) * 100)
                            : 0;
                        return `${percentage}% dos diagnósticos estão como '${sorted[0].label}'`;
                      })()
                    : "Distribuição por Status"}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  paragraph
                  sx={{ fontSize: "0.9rem" }}
                >
                  Acompanhe a certeza dos diagnósticos. Um número alto de status
                  "provisório" pode indicar a necessidade de mais exames para
                  confirmação.
                </Typography>
                <Box sx={{ height: 300, width: "100%" }}>
                  {verificationStatusChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={verificationStatusChartData}
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" allowDecimals={false} />
                        <YAxis
                          dataKey="label"
                          type="category"
                          width={calculateYAxisWidth(
                            verificationStatusChartData
                          )}
                          tick={{ fontSize: 12 }}
                        />
                        <RechartsTooltip
                          formatter={(value: number | undefined) => {
                            if (value === undefined) return ["0", "Quantidade"];
                            const total = verificationStatusChartData.reduce(
                              (acc, item) => acc + item.value,
                              0
                            );
                            const percentage =
                              total > 0 ? Math.round((value / total) * 100) : 0;
                            return [`${value} (${percentage}%)`, "Quantidade"];
                          }}
                        />
                        <Bar dataKey="value" name="Quantidade">
                          {verificationStatusChartData.map((entry, index) => {
                            const maxValue = Math.max(
                              ...verificationStatusChartData.map((d) => d.value)
                            );
                            const isHighlight = entry.value === maxValue;
                            return (
                              <Cell
                                key={`cell-${index}`}
                                fill={
                                  isHighlight
                                    ? theme.palette.primary.main
                                    : "#9E9E9E"
                                }
                              />
                            );
                          })}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        Nenhum dado disponível
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Gráfico de Top 5 Diagnósticos */}
                {topDiagnosticsData.length > 0 && <Divider sx={{ my: 3 }} />}

                {topDiagnosticsData.length > 0 ? (
                  <Box>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: "bold" }}
                    >
                      {(() => {
                        const total = allDiagnosticsData.reduce(
                          (acc, item) => acc + item.value,
                          0
                        );
                        const percentage =
                          total > 0
                            ? Math.round(
                                (topDiagnosticsData[0].value / total) * 100
                              )
                            : 0;
                        return `'${topDiagnosticsData[0].label}' representa ${percentage}% dos diagnósticos`;
                      })()}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      paragraph
                      sx={{ fontSize: "0.9rem" }}
                    >
                      Veja os 5 diagnósticos mais registrados (códigos CID). Use
                      a tabela abaixo para pesquisar todos os diagnósticos.
                    </Typography>
                    <Box sx={{ height: 250, width: "100%", mt: 2 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={topDiagnosticsData}
                          layout="vertical"
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" allowDecimals={false} />
                          <YAxis
                            dataKey="label"
                            type="category"
                            width={calculateYAxisWidth(topDiagnosticsData)}
                            tick={{ fontSize: 12 }}
                          />
                          <RechartsTooltip
                            formatter={(value: number | undefined) => {
                              if (value === undefined)
                                return ["0", "Quantidade"];
                              const total = allDiagnosticsData.reduce(
                                (acc, item) => acc + item.value,
                                0
                              );
                              const percentage =
                                total > 0
                                  ? Math.round((value / total) * 100)
                                  : 0;
                              return [
                                `${value} (${percentage}%)`,
                                "Quantidade",
                              ];
                            }}
                          />
                          <Bar dataKey="value" name="Quantidade">
                            {topDiagnosticsData.map((entry, index) => {
                              const maxValue = Math.max(
                                ...topDiagnosticsData.map((d) => d.value)
                              );
                              const isHighlight = entry.value === maxValue;
                              return (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={
                                    isHighlight
                                      ? theme.palette.primary.main
                                      : "#9E9E9E"
                                  }
                                />
                              );
                            })}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>

                    {/* Tabela de todos os diagnósticos */}
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Todos os Diagnósticos ({allDiagnosticsData.length} tipos
                        diferentes)
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <TextField
                          size="small"
                          variant="outlined"
                          fullWidth
                          placeholder="Buscar diagnóstico..."
                          value={diagnosticSearch}
                          onChange={(e) => setDiagnosticSearch(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                      <GenericTable
                        tableColumns={[
                          {
                            column: "Diagnóstico",
                            rowName: "label",
                            align: "left",
                          },
                          {
                            column: "Quantidade",
                            rowName: "value",
                            width: "120px",
                          },
                          {
                            column: "Percentual",
                            rowName: "percentage",
                            width: "120px",
                          },
                        ]}
                        tableRows={filteredDiagnosticsData.map((item) => {
                          const total = allDiagnosticsData.reduce(
                            (acc, i) => acc + i.value,
                            0
                          );
                          const percentage =
                            total > 0
                              ? Math.round((item.value / total) * 100)
                              : 0;
                          return {
                            ...item,
                            percentage: `${percentage}%`,
                          };
                        })}
                        characterLimit={80}
                      />
                    </Box>
                  </Box>
                ) : (
                  <NoData message="Sem dados de diagnósticos registrados." />
                )}
              </CardContent>
            </Card>
          </TabPanel>

          {/* Aba de Detalhamento */}
          <TabPanel value={currentTab} index={1}>
            <Card>
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <TextField
                    size="small"
                    variant="outlined"
                    fullWidth
                    placeholder="Buscar por paciente ou diagnóstico"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <GenericTable
                  tableColumns={[
                    {
                      column: "Paciente",
                      rowName: "patientName",
                      align: "left",
                    },
                    {
                      column: "Confirmado",
                      rowName: "confirmed",
                      width: "100px",
                    },
                    {
                      column: "Não Confirmado",
                      rowName: "unconfirmed",
                      width: "130px",
                    },
                    {
                      column: "Provisório",
                      rowName: "provisional",
                      width: "100px",
                    },
                    { column: "Refutado", rowName: "refuted", width: "100px" },
                    {
                      column: "Total",
                      rowName: "totalDiagnostics",
                      width: "80px",
                    },
                  ]}
                  tableRows={filteredPatientGroups}
                  characterLimit={60}
                  onRowClick={handlePatientRowClick}
                />
              </CardContent>
            </Card>
          </TabPanel>
        </>
      ) : (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Nenhum diagnóstico encontrado
          </Typography>
        </Box>
      )}

      {/* Dialog para exibir diagnósticos do paciente */}
      {patientDialogOpen && selectedPatient && (
        <Dialog
          open={patientDialogOpen}
          onClose={() => setPatientDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Diagnósticos de: {selectedPatient.patientName}
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 2 }}>
              <TextField
                size="small"
                variant="outlined"
                fullWidth
                placeholder="Buscar por diagnóstico, código ou status"
                value={dialogSearch}
                onChange={(e) => setDialogSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {filteredDialogDiagnostics.length > 0 ? (
              <GenericTable
                tableColumns={[
                  {
                    column: "Diagnóstico",
                    rowName: "diagnostic",
                    align: "left",
                  },
                  { column: "Código", rowName: "code", width: "120px" },
                  { column: "Status", rowName: "statusLabel", width: "150px" },
                  { column: "Data", rowName: "date", width: "120px" },
                ]}
                tableRows={filteredDialogDiagnostics.map((diagnostic) => ({
                  diagnostic: diagnostic.diagnostic,
                  code: diagnostic.code,
                  statusLabel:
                    useDict.verificationStatus[diagnostic.verificationStatus] ||
                    diagnostic.verificationStatus ||
                    "Não informado",
                  date: diagnostic.date,
                }))}
                characterLimit={80}
              />
            ) : (
              <Typography variant="body1" align="center" sx={{ py: 2 }}>
                {dialogSearch
                  ? "Nenhum diagnóstico encontrado com os critérios de busca."
                  : "Nenhum diagnóstico encontrado para este paciente."}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setPatientDialogOpen(false);
                setSelectedPatient(null);
                setPatientDiagnostics([]);
                setDialogSearch("");
              }}
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Stack>
  );
}
